// frontend/src/components/AIOperationsHub.jsx
// Version: 2.1 (Task T3 — переведён на дизайн-систему §I.4: PageHeader, MetricCard, Card, Button, Badge, EmptyState)
// Description: Единый операционный командный центр для всех AI-агентов (Katifi, Sofia Voice,
//              Elena Paralegal, AI Council): плеер записей звонков, интерактивный транскрипт,
//              генерация attorney notes, консилиум по рискам и live-сигналы.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import apiService from '../services/apiService';
import { useBranding } from '../context/BrandingContext';
import { paralegalSkryt } from '../utils/paralegalScreen';
import { useUiLanguage } from '../context/UiLanguageContext';
import useNavCounters from '../hooks/useNavCounters';
import { useCallHistory } from '../hooks/useVoiceAPI';
import { formatPhoneNumber, formatDate } from '../lib/utils';
import { formatVapiEndedReason } from '../i18n/vapi-reasons';
import {
  PageHeader,
  MetricCard,
  Card,
  Button,
  Badge,
  EmptyState
} from './ui';
import {
  Layers,
  Sparkles,
  Bot,
  PhoneCall,
  FileCheck2,
  Scale,
  Play,
  Pause,
  Volume2,
  VolumeX,
  ShieldCheck,
  MapPin,
  X,
  FileText,
  Sliders,
  Radio,
  ArrowRight,
  Briefcase,
  AlertTriangle
} from 'lucide-react';

// Встроенные роли системы — показываются, ТОЛЬКО когда реестр агентов пуст.
const VSTROENNYE_ROLI = [
  { kluch: 'katifi', name: 'Katifi', tone: 'bg-primary text-on-primary', icon: Sparkles },
  { kluch: 'sofia', name: 'Sofia Voice', tone: 'bg-accent-soft text-accent-ink', icon: PhoneCall },
  { kluch: 'paralegal', paralegal: true, tone: 'bg-surface-sunken text-info', icon: FileCheck2 },
  { kluch: 'council', name: 'AI Council', tone: 'bg-surface-sunken text-accent-ink', icon: Scale }
];

// Отбор журнала звонков. Объявлен ВНЕ компонента нарочно.
const OTBOR_ZVONKOV = {};

const formatDuration = (sec) => {
  const s = Number(sec);
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const celye = Math.floor(s);
  return `${Math.floor(celye / 60)}:${String(celye % 60).padStart(2, '0')}`;
};

const podpisZvonka = (call) => formatPhoneNumber(call?.caller_number);

const replikiZvonka = (call) => {
  const soobshcheniya = call?.transcript_json?.messages;
  return Array.isArray(soobshcheniya) ? soobshcheniya : [];
};

export default function AIOperationsHub({ onAskKatifi, onGoToCase }) {
  const navigate = useNavigate();

  const { features } = useBranding();
  const { t, lang } = useUiLanguage();
  const vstroennyeRoli = useMemo(() => {
    return VSTROENNYE_ROLI
      .filter((a) => !(paralegalSkryt(features) && a.paralegal))
      .map((a) => ({
        ...a,
        name: a.name || t(`aiops.agent.${a.kluch}.name`),
        role: t(`aiops.agent.${a.kluch}.role`),
        detail: t(`aiops.agent.${a.kluch}.detail`),
        vstroennaya: true
      }));
  }, [features, t]);

  const navCounters = useNavCounters();
  const [liveAgents, setLiveAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [agentsError, setAgentsError] = useState(false);

  useEffect(() => {
    let active = true;
    apiService.getAgents()
      .then((data) => {
        if (active && Array.isArray(data)) {
          setLiveAgents(data);
        }
      })
      .catch((err) => {
        console.error('Failed to load agents in AIOperationsHub:', err);
        if (active) setAgentsError(true);
      })
      .finally(() => {
        if (active) setAgentsLoading(false);
      });
    return () => { active = false; };
  }, []);

  const [searchParams] = useSearchParams();
  const caseIdParam = searchParams.get('case');
  const caseTitleParam = searchParams.get('title');
  const caseClientParam = searchParams.get('client');
  const tabParam = searchParams.get('tab');

  const [activeTab, setActiveTab] = useState(
    tabParam || (caseIdParam ? 'council' : 'team')
  );

  useEffect(() => {
    if (tabParam) {
      setActiveTab(tabParam);
    } else if (caseIdParam) {
      setActiveTab('council');
    }
  }, [tabParam, caseIdParam]);

  const {
    calls: zvonki,
    loading: zvonkiLoading,
    error: zvonkiError
  } = useCallHistory('default', OTBOR_ZVONKOV);

  const [selectedCallId, setSelectedCallId] = useState(null);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [selectedLines, setSelectedLines] = useState([]);
  const [generatedNote, setGeneratedNote] = useState(null);
  const [showCouncilModal, setShowCouncilModal] = useState(false);
  const [showGuardrailsModal, setShowGuardrailsModal] = useState(false);

  const activeCall = useMemo(
    () => (zvonki || []).find((r) => r.id === selectedCallId) || null,
    [zvonki, selectedCallId]
  );

  useEffect(() => {
    if (!selectedCallId && Array.isArray(zvonki) && zvonki.length > 0) {
      setSelectedCallId(zvonki[0].id);
    }
  }, [zvonki, selectedCallId]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.load();
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(activeCall?.duration_seconds || 0);
    setSelectedLines([]);
    setGeneratedNote(null);
  }, [selectedCallId, activeCall]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          console.warn('Playback error:', err);
          setIsPlaying(false);
        });
    }
  };

  const seekTo = (seconds, autoPlay = false) => {
    const targetDuration = duration || activeCall?.duration_seconds || 0;
    const clamped = Math.max(0, Math.min(seconds, targetDuration));
    if (audioRef.current) {
      audioRef.current.currentTime = clamped;
      if (autoPlay && !isPlaying) {
        audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
      }
    }
    setCurrentTime(clamped);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleSelectCall = (id) => {
    setSelectedCallId(id);
  };

  // Переключение выбора реплики в расшифровке. Опознаватель — ПОРЯДКОВЫЙ НОМЕР
  // реплики: метки времени у живых реплик нет, а текст повторяется («Yes»).
  const toggleLine = (nomerRepliki) => {
    setSelectedLines((prev) =>
      prev.includes(nomerRepliki)
        ? prev.filter((i) => i !== nomerRepliki)
        : [...prev, nomerRepliki]
    );
    setGeneratedNote(null);
  };

  // Черновик заметки адвоката из выбранных реплик.
  //
  // 🔴 НИ ОДНОГО ВЫДУМАННОГО СЛОВА. Прежняя сборка подставляла готовые «summary»
  //    и «Recommended next step» по признаку `id === 'sofia-parole'` — то есть
  //    юридический вывод НЕ вытекал из разговора, а был написан заранее. В
  //    черновике остаются ТОЛЬКО выбранные реплики: их сказал звонивший, их
  //    можно проверить по записи.
  const handleGenerateNote = () => {
    if (!activeCall || !selectedLines.length) return;
    const repliki = replikiZvonka(activeCall);
    const sortedIndices = [...selectedLines].sort((a, b) => a - b);
    const facts = sortedIndices
      .map((idx) => {
        const m = repliki[idx];
        const speaker = m?.role === 'assistant' ? t('aiops.voice.speaker_sofia') : t('aiops.voice.speaker_caller');
        return `${speaker}: ${m?.content || ''}`;
      });

    setGeneratedNote({
      callId: activeCall.id,
      zvonok: podpisZvonka(activeCall),
      kogda: formatDate(activeCall.created_at),
      facts
    });
    // 🔴 РАНЬШЕ ЗДЕСЬ СТОЯЛО УТВЕРЖДЕНИЕ О СОХРАНЕНИИ В ИСТОРИЮ — и это была НЕПРАВДА:
    //    ручки сохранения нет, черновик живёт в состоянии вкладки и умирает при
    //    переключении звонка. Обещание, которого никто не исполняет, хуже
    //    молчания: по нему человек перестаёт записывать сам.
    toast(t('aiops.note.toast'), {
      icon: '📝'
    });
  };

  const handleKatifiQuery = (prompt) => {
    if (onAskKatifi) {
      onAskKatifi(prompt);
    } else {
      window.dispatchEvent(new CustomEvent('katifi:open', { detail: { tekst: prompt } }));
      toast(t('aiops.katifi.toast', { prompt }), {
        icon: '✨'
      });
    }
  };

  const handleNavigateCase = () => {
    if (onGoToCase) {
      onGoToCase();
    } else {
      navigate('/cases');
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* 1. Заголовок и вызов Katifi */}
      <PageHeader
        kicker={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, color: 'var(--accent-ink)' }}>
            <Layers style={{ width: '0.875rem', height: '0.875rem' }} />
            {t('aiops.header.kicker')}
          </span>
        }
        title={t('aiops.header.title')}
        subtitle={t('aiops.header.subtitle')}
        actions={
          <Button
            variant="primary"
            onClick={() => handleKatifiQuery(t('aiops.header.ask_katifi_prompt'))}
            startIcon={<Sparkles style={{ width: '0.875rem', height: '0.875rem', color: 'var(--accent)' }} />}
          >
            {t('aiops.header.ask_katifi')}
          </Button>
        }
      />

      {/* 2. Три ключевые операционные метрики */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <MetricCard
          label={t('aiops.metric.decision')}
          value={navCounters?.inbox?.value ?? '—'}
          subtext={navCounters?.inbox?.value === undefined ? t('aiops.metric.decision_no_answer') : t('aiops.metric.decision_in_review')}
        />

        <MetricCard
          label={t('aiops.metric.roster')}
          value={agentsLoading ? '…' : agentsError ? '—' : liveAgents.length}
          subtext={
            agentsLoading
              ? t('aiops.metric.roster_loading')
              : agentsError
              ? t('aiops.metric.roster_error')
              : liveAgents.length > 0
              ? t('aiops.metric.roster_operational', { n: liveAgents.filter((a) => a.is_active !== false).length })
              : t('aiops.metric.roster_empty')
          }
        />

        <MetricCard
          label={t('aiops.metric.cases')}
          value={navCounters?.cases?.value ?? '—'}
          subtext={
            navCounters?.clients?.value
              ? t('aiops.metric.cases_clients', { n: navCounters.clients.value })
              : t('aiops.metric.cases_no_clients')
          }
        />
      </section>

      {/* 3. Основная рабочая область (Табы + Правый сайдбар сигналов) */}
      <section className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-5 items-start">
        {/* Левая колонка с табами */}
        <Card className="overflow-hidden">
          {/* Переключатель табов */}
          <div
            className="px-5 pt-3 flex items-center gap-1 overflow-x-auto"
            style={{ borderBottom: '1px solid var(--border)' }}
          >
            {[
              ['team', `${t('aiops.tab.team')}${liveAgents.length > 0 ? ` (${liveAgents.length})` : ''}`],
              ['voice', t('aiops.tab.voice')],
              ...(paralegalSkryt(features) ? [] : [['paralegal', t('aiops.tab.paralegal')]]),
              ['council', t('aiops.tab.council')]
            ].map(([tabKey, tabLabel]) => (
              <button
                key={tabKey}
                onClick={() => setActiveTab(tabKey)}
                className={`px-3 py-3 text-xs font-bold border-b-2 whitespace-nowrap transition-colors cursor-pointer ${
                  activeTab === tabKey
                    ? 'border-accent text-text'
                    : 'border-transparent text-text-muted hover:text-text'
                }`}
                style={{
                  borderBottomColor: activeTab === tabKey ? 'var(--accent)' : 'transparent',
                  color: activeTab === tabKey ? 'var(--text)' : 'var(--text-muted)'
                }}
              >
                {tabLabel}
              </button>
            ))}
          </div>

          {/* Таб 1: AI Team */}
          {activeTab === 'team' && (
            <div className="p-5 space-y-3.5">
              {!agentsLoading && liveAgents.length === 0 && (
                <p
                  data-aiops-builtin
                  className="text-[11px] italic p-3 rounded-xl leading-relaxed"
                  style={{
                    color: 'var(--text-muted)',
                    border: '1px dashed var(--border)',
                    backgroundColor: 'var(--surface-sunken)'
                  }}
                >
                  {t(agentsError ? 'aiops.team.builtin_notice_error' : 'aiops.team.builtin_notice')}
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {(liveAgents.length > 0 ? liveAgents : vstroennyeRoli).map((agent) => {
                  const IconComponent = agent.icon || Bot;
                  const status = agent.vstroennaya ? null : (agent.is_active !== false ? 'active' : 'standby');
                  const xp = agent.vstroennaya ? null : agent.compute_score;
                  const role = agent.specialized_role || agent.role || t('aiops.team.role_default');
                  const detail = agent.persona || agent.detail || t('aiops.team.detail_default');

                  return (
                    <div
                      key={agent.id || agent.name}
                      className="p-4 rounded-xl flex flex-col justify-between transition-colors shadow-2xs"
                      style={{
                        border: '1px solid var(--border)',
                        backgroundColor: 'var(--surface-raised)'
                      }}
                    >
                      <div>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3">
                            {agent.avatar_url ? (
                              <img
                                src={agent.avatar_url}
                                alt={agent.name}
                                className="w-10 h-10 rounded-lg object-cover shadow-2xs shrink-0"
                                style={{ border: '1px solid var(--border)' }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div
                                className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-xs shrink-0"
                                style={{
                                  backgroundColor: 'var(--surface-sunken)',
                                  color: 'var(--accent-ink)'
                                }}
                              >
                                <IconComponent className="w-5 h-5" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <h2
                                  className="text-sm font-bold truncate"
                                  style={{ color: 'var(--text)' }}
                                >
                                  {agent.name}
                                </h2>
                                {agent.is_verified && (
                                  <ShieldCheck
                                    className="w-3.5 h-3.5 shrink-0"
                                    style={{ color: 'var(--info)' }}
                                    title={t('aiops.team.verified')}
                                  />
                                )}
                              </div>
                              <p
                                className="text-[11px] font-semibold truncate"
                                style={{ color: 'var(--text-muted)' }}
                              >
                                {role}
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-col items-end gap-1 shrink-0">
                            {xp != null && (
                              <span
                                className="text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-2xs"
                                style={{
                                  backgroundColor: 'var(--accent-soft)',
                                  color: 'var(--accent-ink)',
                                  border: '1px solid var(--accent-border)'
                                }}
                              >
                                ⚡ XP: {xp}
                              </span>
                            )}
                            {status && (
                              <Badge variant={status === 'active' ? 'success' : 'secondary'}>
                                {t(`aiops.team.status_${status}`)}
                              </Badge>
                            )}
                          </div>
                        </div>

                        <p
                          className="text-xs leading-relaxed mt-3 line-clamp-3"
                          style={{ color: 'var(--text-muted)' }}
                        >
                          {detail}
                        </p>
                      </div>

                      {(agent.current_location || agent.llm_model) && (
                        <div
                          className="mt-3.5 pt-2.5 flex items-center justify-between text-[10px]"
                          style={{
                            borderTop: '1px solid var(--border)',
                            color: 'var(--text-muted)'
                          }}
                        >
                          <span className="inline-flex items-center gap-1">
                            {agent.current_location && <MapPin className="w-3 h-3 text-accent-ink" />}
                            {agent.current_location}
                          </span>
                          {agent.llm_model && (
                            <span
                              className="font-mono text-[9px] px-1.5 py-0.5 rounded"
                              style={{
                                backgroundColor: 'var(--surface-sunken)',
                                border: '1px solid var(--border)',
                                color: 'var(--text)'
                              }}
                            >
                              {agent.llm_model}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Таб 2: Voice AI */}
          {activeTab === 'voice' && (
            <div className="p-5 space-y-4">
              <div
                className="flex items-center gap-3 p-4 rounded-xl"
                style={{
                  backgroundColor: 'var(--accent-soft)',
                  border: '1px solid var(--accent-border)'
                }}
              >
                <PhoneCall className="w-5 h-5 shrink-0" style={{ color: 'var(--accent-ink)' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold" style={{ color: 'var(--text)' }}>
                    {t('aiops.voice.title')}
                  </p>
                  <p className="text-[11px] mt-0.5 leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                    {t('aiops.voice.languages')}
                  </p>
                </div>
              </div>

              {/* Двухколоночный интерфейс: Список звонков + Аудиоплеер и Транскрипт */}
              <div className="grid grid-cols-1 lg:grid-cols-[210px_minmax(0,1fr)] gap-4">
                {/* Список звонков */}
                <aside className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-bold" style={{ color: 'var(--text)' }}>
                      {t('aiops.voice.recent')}
                    </h3>
                    <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                      {zvonkiLoading || zvonkiError ? '' : t('aiops.voice.count', { n: zvonki.length })}
                    </span>
                  </div>

                  {zvonkiLoading && (
                    <p
                      className="text-[11px] italic p-3 rounded-xl"
                      style={{
                        color: 'var(--text-muted)',
                        border: '1px dashed var(--border)'
                      }}
                    >
                      {t('aiops.voice.loading')}
                    </p>
                  )}

                  {!zvonkiLoading && zvonkiError && (
                    <p
                      className="text-[11px] p-3 rounded-xl leading-relaxed"
                      style={{
                        color: 'var(--danger)',
                        border: '1px solid var(--danger-border)',
                        backgroundColor: 'var(--danger-soft)'
                      }}
                    >
                      {t('aiops.voice.error', { error: zvonkiError })}
                    </p>
                  )}

                  {!zvonkiLoading && !zvonkiError && zvonki.length === 0 && (
                    <p
                      className="text-[11px] italic p-3 rounded-xl"
                      style={{
                        color: 'var(--text-muted)',
                        border: '1px dashed var(--border)'
                      }}
                    >
                      {t('aiops.voice.empty')}
                    </p>
                  )}

                  {!zvonkiLoading && !zvonkiError && zvonki.map((call) => (
                    <button
                      key={call.id}
                      onClick={() => handleSelectCall(call.id)}
                      className="w-full p-3 rounded-xl border text-left transition-colors cursor-pointer"
                      style={{
                        borderColor: activeCall?.id === call.id ? 'var(--accent)' : 'var(--border)',
                        backgroundColor: activeCall?.id === call.id ? 'var(--accent-soft)' : 'var(--surface-raised)'
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-[11px] font-bold truncate" style={{ color: 'var(--text)' }}>
                          {podpisZvonka(call)}
                        </span>
                        <span className="text-[10px] shrink-0" style={{ color: 'var(--text-muted)' }}>
                          {formatDuration(call.duration_seconds)}
                        </span>
                      </div>
                      <div className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
                        {formatDate(call.created_at)}
                      </div>
                      <div className="text-[10px] font-semibold mt-1" style={{ color: 'var(--accent-ink)' }}>
                        {call.outcome || call.status || call.direction || t('aiops.voice.no_outcome')}
                      </div>
                    </button>
                  ))}
                </aside>

                {/* Плеер и детали звонка */}
                <div
                  className="rounded-xl overflow-hidden flex flex-col justify-between"
                  style={{ border: '1px solid var(--border)', backgroundColor: 'var(--surface-raised)' }}
                >
                  {!activeCall ? (
                    <div className="p-8 text-center text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                      {zvonkiLoading
                        ? t('aiops.voice.loading')
                        : zvonkiError
                        ? t('aiops.voice.error', { error: zvonkiError })
                        : t('aiops.voice.empty')}
                    </div>
                  ) : (
                    <>
                      <div
                        className="p-4 flex flex-col sm:flex-row sm:items-start justify-between gap-2"
                        style={{ borderBottom: '1px solid var(--border)' }}
                      >
                        <div>
                          <h3 className="text-sm font-bold" style={{ color: 'var(--text)' }}>
                            {podpisZvonka(activeCall)}
                          </h3>
                          <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                            {formatDate(activeCall.created_at)}
                            {activeCall.direction ? ` · ${activeCall.direction}` : ''}
                            {activeCall.outcome ? ` · ${activeCall.outcome}` : ''}
                          </p>
                        </div>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => navigate('/history')}
                          style={{ padding: 0, height: 'auto', fontWeight: 700 }}
                        >
                          {t('aiops.voice.open_history')}
                        </Button>
                      </div>

                      <div className="p-4 space-y-3">
                        <p className="text-xs leading-relaxed" style={{ color: 'var(--text)' }}>
                          <strong style={{ color: 'var(--text)' }}>{t('aiops.voice.signs')}</strong>{' '}
                          {[
                            activeCall.lead_intent && t('aiops.voice.sign_intent', { v: activeCall.lead_intent }),
                            activeCall.sentiment && t('aiops.voice.sign_sentiment', { v: activeCall.sentiment }),
                            activeCall.ended_reason && t('aiops.voice.sign_ended', { v: formatVapiEndedReason(activeCall.ended_reason, lang) })
                          ].filter(Boolean).join(' · ') || t('aiops.voice.signs_none')}
                        </p>

                        {/* Тёмный карточный плеер */}
                        {(activeCall.vapi_call_id || activeCall.recording_url) ? (
                          <div
                            className="rounded-xl p-4 shadow-md text-white"
                            style={{ backgroundColor: 'var(--primary)' }}
                          >
                            <audio
                              ref={audioRef}
                              src={activeCall.id ? `/api/v1/voice/calls/${activeCall.id}/recording` : activeCall.recording_url}
                              preload="auto"
                              onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                              onLoadedMetadata={(e) => {
                                if (e.currentTarget.duration && !isNaN(e.currentTarget.duration)) {
                                  setDuration(Math.round(e.currentTarget.duration));
                                }
                              }}
                              onEnded={() => {
                                setIsPlaying(false);
                                setCurrentTime(0);
                              }}
                              onPlay={() => setIsPlaying(true)}
                              onPause={() => setIsPlaying(false)}
                            />

                            <div className="flex items-center gap-3">
                              <button
                                onClick={togglePlay}
                                className="w-9 h-9 rounded-full grid place-items-center cursor-pointer shrink-0 transition-all shadow-sm active:scale-95"
                                style={{
                                  backgroundColor: 'var(--accent)',
                                  color: 'var(--primary)'
                                }}
                                aria-label={isPlaying ? t('aiops.voice.pause') : t('aiops.voice.play')}
                                title={isPlaying ? t('aiops.voice.pause') : t('aiops.voice.play')}
                              >
                                {isPlaying ? (
                                  <Pause className="w-4 h-4 fill-current" />
                                ) : (
                                  <Play className="w-4 h-4 fill-current ml-0.5" />
                                )}
                              </button>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between text-[10px] text-white/65 font-mono">
                                  <span>{formatDuration(Math.floor(currentTime))}</span>
                                  <span>{formatDuration(duration || activeCall.duration_seconds)}</span>
                                </div>
                                <input
                                  aria-label={t('aiops.voice.progress')}
                                  type="range"
                                  min="0"
                                  max={duration || activeCall.duration_seconds || 0}
                                  value={currentTime}
                                  onChange={(e) => seekTo(Number(e.target.value))}
                                  className="mt-1 w-full cursor-pointer"
                                  style={{ accentColor: 'var(--accent)' }}
                                />
                              </div>

                              <button
                                onClick={toggleMute}
                                className="text-white/65 hover:text-white transition-colors cursor-pointer shrink-0 p-1"
                                title={isMuted ? t('aiops.voice.unmute') : t('aiops.voice.mute')}
                                aria-label={isMuted ? t('aiops.voice.unmute') : t('aiops.voice.mute')}
                              >
                                {isMuted ? (
                                  <VolumeX className="w-4 h-4" style={{ color: 'var(--danger)' }} />
                                ) : (
                                  <Volume2 className="w-4 h-4" />
                                )}
                              </button>
                            </div>

                            {/* Waveform */}
                            <div className="h-6 mt-3 flex items-center gap-0.5">
                              {Array.from({ length: 54 }, (_, i) => {
                                const barTime = Math.round((i / 54) * (duration || activeCall.duration_seconds || 0));
                                const isPassed = currentTime >= barTime;
                                return (
                                  <button
                                    key={i}
                                    onClick={() => seekTo(barTime)}
                                    className="flex-1 rounded-xs transition-all cursor-pointer"
                                    style={{
                                      height: `${20 + ((i * 17) % 45)}%`,
                                      backgroundColor: isPassed ? 'var(--accent)' : 'rgba(255, 255, 255, 0.2)'
                                    }}
                                    aria-label={t('aiops.voice.seek', { n: barTime })}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <p
                            className="text-[11px] italic rounded-xl p-3"
                            style={{
                              color: 'var(--text-muted)',
                              border: '1px dashed var(--border)'
                            }}
                          >
                            {t('aiops.voice.no_audio')}
                          </p>
                        )}
                      </div>

                      {/* Расшифровка с выбором реплик */}
                      <div
                        className="max-h-60 overflow-y-auto"
                        style={{ borderTop: '1px solid var(--border)' }}
                      >
                        <div
                          className="p-3 flex flex-wrap items-center justify-between gap-2 sticky top-0 z-10"
                          style={{
                            backgroundColor: 'var(--surface-raised)',
                            borderBottom: '1px solid var(--border)'
                          }}
                        >
                          <div>
                            <span className="text-xs font-bold" style={{ color: 'var(--text)' }}>
                              {t('aiops.voice.transcript')}
                            </span>
                            <span className="block text-[10px]" style={{ color: 'var(--text-muted)' }}>
                              {t('aiops.voice.transcript_hint')}
                            </span>
                          </div>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleGenerateNote}
                            disabled={!selectedLines.length}
                            startIcon={<FileText className="w-3 h-3" />}
                          >
                            {t('aiops.voice.generate')}{' '}
                            {selectedLines.length ? `(${selectedLines.length})` : ''}
                          </Button>
                        </div>

                        <div className="p-3 space-y-1.5">
                          {replikiZvonka(activeCall).length === 0 ? (
                            <p className="text-[11px] italic py-2" style={{ color: 'var(--text-muted)' }}>
                              {t('aiops.voice.no_transcript')}
                            </p>
                          ) : (
                            replikiZvonka(activeCall).map((m, idx) => {
                              const isSelected = selectedLines.includes(idx);
                              const govoritSofia = m.role === 'assistant';

                              return (
                                <label
                                  key={idx}
                                  className="grid grid-cols-[16px_84px_minmax(0,1fr)] gap-2 text-[11px] rounded-lg p-1.5 transition-all cursor-pointer"
                                  style={{
                                    backgroundColor: isSelected ? 'var(--accent-soft)' : 'transparent',
                                    border: isSelected ? '1px solid var(--accent-border)' : '1px solid transparent'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleLine(idx)}
                                    className="mt-0.5 cursor-pointer"
                                    style={{ accentColor: 'var(--accent-ink)' }}
                                    aria-label={t('aiops.voice.select_line', { n: idx + 1 })}
                                  />
                                  <span
                                    className="font-bold"
                                    style={{ color: govoritSofia ? 'var(--info)' : 'var(--text)' }}
                                  >
                                    {govoritSofia ? t('aiops.voice.speaker_sofia') : t('aiops.voice.speaker_caller')}
                                  </span>
                                  <span className="leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                                    {m.content}
                                  </span>
                                </label>
                              );
                            })
                          )}
                        </div>
                      </div>

                      {/* Черновик заметки адвоката */}
                      {generatedNote && (
                        <div
                          className="m-4 p-4 rounded-xl space-y-3 animate-in fade-in"
                          style={{
                            border: '1px solid var(--accent-border)',
                            backgroundColor: 'var(--accent-soft)'
                          }}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2 text-xs font-bold" style={{ color: 'var(--accent-ink)' }}>
                              <Sparkles className="w-4 h-4" />
                              {t('aiops.note.title')}
                            </div>
                            <span className="text-[10px] font-bold" style={{ color: 'var(--accent-ink)' }}>
                              {t('aiops.note.not_saved')}
                            </span>
                          </div>

                          <div>
                            <div className="text-[10px] uppercase tracking-wide font-bold" style={{ color: 'var(--text-muted)' }}>
                              {t('aiops.note.call')}
                            </div>
                            <p className="text-xs leading-relaxed mt-0.5" style={{ color: 'var(--text)' }}>
                              {generatedNote.zvonok} · {generatedNote.kogda}
                            </p>
                          </div>

                          <div>
                            <div className="text-[10px] uppercase tracking-wide font-bold" style={{ color: 'var(--text-muted)' }}>
                              {t('aiops.note.lines')}
                            </div>
                            <ul className="mt-1 space-y-0.5">
                              {generatedNote.facts.map((f, i) => (
                                <li
                                  key={i}
                                  className="text-[11px] leading-relaxed"
                                  style={{ color: 'var(--text-muted)' }}
                                >
                                  • {f}
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div className="flex flex-wrap items-center gap-3 pt-1">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => navigate('/history')}
                              startIcon={<FileText className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} />}
                            >
                              {t('aiops.note.open_history')}
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() =>
                                handleKatifiQuery(
                                  t('aiops.note.katifi_prompt', { call: generatedNote.zvonok, when: generatedNote.kogda, lines: generatedNote.facts.join(' | ') })
                                )
                              }
                              style={{ color: 'var(--accent-ink)', fontWeight: 700 }}
                            >
                              {t('aiops.note.open_katifi')}
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <Button
                variant="link"
                size="sm"
                onClick={() => navigate('/inbox')}
                endIcon={<ArrowRight className="w-3.5 h-3.5" />}
                style={{ color: 'var(--accent-ink)', fontWeight: 700, padding: 0 }}
              >
                {t('aiops.voice.review_inbox')}
              </Button>
            </div>
          )}

          {/* Таб 3: Paralegal Queue */}
          {activeTab === 'paralegal' && (
            <div className="p-5">
              <div
                className="p-4 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                style={{
                  border: '1px dashed var(--border)',
                  backgroundColor: 'var(--surface-sunken)'
                }}
              >
                <div className="flex items-start gap-3">
                  <FileCheck2
                    className="w-5 h-5 shrink-0 mt-0.5"
                    style={{ color: 'var(--text-muted)' }}
                  />
                  <div>
                    <p className="text-xs font-bold" style={{ color: 'var(--text)' }}>
                      {t('aiops.paralegal.title')}
                    </p>
                    <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                      {t('aiops.paralegal.body')}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNavigateCase}
                  endIcon={<ArrowRight className="w-3.5 h-3.5" />}
                  className="shrink-0"
                >
                  {t('aiops.open_cases')}
                </Button>
              </div>
            </div>
          )}

          {/* Таб 4: Council & Decision Memos */}
          {activeTab === 'council' && (
            <div className="p-5 space-y-4">
              {caseIdParam && (
                <div
                  className="p-3.5 rounded-xl flex items-center justify-between gap-3 shadow-2xs"
                  style={{
                    backgroundColor: 'var(--accent-soft)',
                    border: '1px solid var(--accent-border)'
                  }}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Briefcase
                      className="w-4 h-4 shrink-0"
                      style={{ color: 'var(--accent-ink)' }}
                    />
                    <div className="text-xs truncate" style={{ color: 'var(--text)' }}>
                      {t('aiops.council.case_context')}{' '}
                      <strong className="font-bold">
                        {t('aiops.council.case_n', { n: caseIdParam })} • {caseTitleParam || t('aiops.council.case_default_title')}
                      </strong>
                      {caseClientParam && (
                        <span style={{ color: 'var(--text-muted)' }}> ({caseClientParam})</span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => navigate(`/cases/${caseIdParam}`)}
                    style={{ color: 'var(--accent-ink)', fontWeight: 700, padding: 0 }}
                  >
                    {t('aiops.council.back_to_case')}
                  </Button>
                </div>
              )}

              <div
                className="rounded-xl p-5 text-white space-y-3"
                style={{ backgroundColor: 'var(--primary)' }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div
                      className="text-[10px] uppercase tracking-wide font-bold"
                      style={{ color: 'var(--accent)' }}
                    >
                      {t('aiops.council.memo')} {caseIdParam ? `• ${t('aiops.council.case_n', { n: caseIdParam })}` : ''}
                    </div>
                    <h2 className="text-base font-bold mt-1 text-white">
                      {caseIdParam
                        ? `${caseTitleParam || t('aiops.council.case_default_title')}${caseClientParam ? ` • ${caseClientParam}` : ''}`
                        : t('aiops.council.no_case')}
                    </h2>
                  </div>
                  <Scale className="w-5 h-5 shrink-0" style={{ color: 'var(--accent)' }} />
                </div>

                <p className="text-xs text-white/70 leading-relaxed">
                  {caseIdParam
                    ? t('aiops.council.case_not_held')
                    : t('aiops.council.open_from_case')}
                </p>

                <div className="pt-2">
                  <Button
                    variant="accent"
                    size="sm"
                    onClick={() => setShowCouncilModal(true)}
                    startIcon={<Scale className="w-4 h-4" />}
                  >
                    {t('aiops.council.what_is')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Правый сайдбар: Live сигналы + Срочные дедлайны + Языковые правила */}
        <aside className="space-y-4">
          {/* Live operating signals */}
          <Card className="p-4 shadow-2xs">
            <div className="flex items-center gap-2 text-xs font-bold" style={{ color: 'var(--text)' }}>
              <Radio className="w-4 h-4" style={{ color: 'var(--success)' }} />
              {t('aiops.signals.title')}
            </div>

            <div className="mt-4 space-y-3">
              {[
                [
                  t('aiops.signals.katifi'),
                  agentsLoading
                    ? t('aiops.metric.roster_loading')
                    : agentsError
                    ? t('aiops.signals.katifi_registry_error')
                    : liveAgents.length > 0
                    ? t('aiops.signals.katifi_registry', { n: liveAgents.length })
                    : t('aiops.signals.katifi_registry_empty'),
                  'amber'
                ],
                [
                  t('aiops.signals.sofia'),
                  zvonkiLoading
                    ? t('aiops.voice.loading')
                    : zvonkiError
                    ? t('aiops.voice.error', { error: zvonkiError })
                    : t('aiops.signals.sofia_calls', { n: (zvonki || []).length }),
                  'green'
                ],
                ...(paralegalSkryt(features) ? [] : [[
                  t('aiops.signals.paralegal'),
                  t('aiops.signals.paralegal_queue'),
                  'blue'
                ]])
              ].map(([agentName, statusText, colorDot]) => (
                <div key={agentName} className="flex items-center gap-2.5">
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{
                      backgroundColor:
                        colorDot === 'green'
                          ? 'var(--success)'
                          : colorDot === 'amber'
                          ? 'var(--warning)'
                          : 'var(--info)'
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold" style={{ color: 'var(--text)' }}>
                      {agentName}
                    </p>
                    <p className="text-[10px] truncate" style={{ color: 'var(--text-muted)' }}>
                      {statusText}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Памятка о сроках */}
          <Card className="p-4 shadow-2xs space-y-2">
            <div className="flex items-center gap-2 text-xs font-bold" style={{ color: 'var(--text-muted)' }}>
              <AlertTriangle className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
              {t('aiops.deadlines.title')}
            </div>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--text)' }}>
              {t('aiops.deadlines.body')}
            </p>
            <Button
              variant="link"
              size="sm"
              onClick={handleNavigateCase}
              style={{ color: 'var(--accent-ink)', fontWeight: 700, padding: 0 }}
            >
              {t('aiops.deadlines.open')}
            </Button>
          </Card>

          {/* AI Language & Guardrails */}
          <button
            type="button"
            onClick={() => setShowGuardrailsModal(true)}
            className="w-full p-4 rounded-xl text-left transition-colors cursor-pointer shadow-2xs block"
            style={{
              border: '1px solid var(--border)',
              backgroundColor: 'var(--surface-raised)'
            }}
          >
            <div className="flex items-center gap-2 text-xs font-bold" style={{ color: 'var(--text)' }}>
              <Sliders className="w-4 h-4" style={{ color: 'var(--accent-ink)' }} />
              {t('aiops.guard.title')}
            </div>
            <p className="text-[11px] mt-1 leading-relaxed" style={{ color: 'var(--text-muted)' }}>
              {t('aiops.guard.subtitle')}
            </p>
          </button>
        </aside>
      </section>

      {/* 4. МОДАЛЬНОЕ ОКНО: что такое консилиум */}
      {showCouncilModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div
            className="rounded-2xl max-w-2xl w-full p-6 space-y-5 shadow-2xl text-white animate-in fade-in zoom-in-95"
            style={{
              backgroundColor: 'var(--primary)',
              border: '1px solid var(--border-strong)'
            }}
          >
            <div
              className="flex items-start justify-between pb-4"
              style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.15)' }}
            >
              <div>
                <div
                  className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider"
                  style={{ color: 'var(--accent)' }}
                >
                  <Scale className="w-4 h-4" />
                  {t('aiops.council.modal_kicker')}
                </div>
                <h2 className="text-xl font-serif font-normal text-white mt-1">
                  {t('aiops.council.modal_title')}
                </h2>
              </div>
              <button
                onClick={() => setShowCouncilModal(false)}
                className="p-1 rounded text-white/60 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div
              className="p-4 rounded-xl space-y-3 text-xs leading-relaxed"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.9)'
              }}
            >
              <p>
                <strong>{t('aiops.council.modal_what')}</strong> {t('aiops.council.modal_what_body')}
              </p>
              <p>
                <strong>{t('aiops.council.modal_missing')}</strong> {t('aiops.council.modal_missing_body')}
              </p>
            </div>

            <div className="flex justify-end pt-2">
              <Button
                variant="accent"
                size="sm"
                onClick={() => setShowCouncilModal(false)}
              >
                {t('aiops.close')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 5. МОДАЛЬНОЕ ОКНО: AI Language & Guardrails */}
      {showGuardrailsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div
            className="rounded-2xl max-w-lg w-full p-6 space-y-5 shadow-2xl animate-in fade-in zoom-in-95"
            style={{
              backgroundColor: 'var(--surface-raised)',
              border: '1px solid var(--border)',
              color: 'var(--text)'
            }}
          >
            <div
              className="flex items-start justify-between pb-3"
              style={{ borderBottom: '1px solid var(--border)' }}
            >
              <div>
                <h3 className="font-serif text-lg font-bold" style={{ color: 'var(--text)' }}>
                  {t('aiops.guard.title')}
                </h3>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  {t('aiops.guard.modal_subtitle')}
                </p>
              </div>
              <button
                onClick={() => setShowGuardrailsModal(false)}
                className="p-1 rounded transition-colors cursor-pointer"
                style={{ color: 'var(--text-muted)' }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div className="space-y-1.5">
                <label className="font-bold block" style={{ color: 'var(--text)' }}>
                  {t('aiops.guard.languages')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {[t('aiops.guard.lang_en'), t('aiops.guard.lang_ru'), t('aiops.guard.lang_es')].map(
                    (langKey) => (
                      <span
                        key={langKey}
                        className="px-2.5 py-1 rounded font-semibold"
                        style={{
                          backgroundColor: 'var(--surface-sunken)',
                          border: '1px solid var(--border)',
                          color: 'var(--text)'
                        }}
                      >
                        ✓ {langKey}
                      </span>
                    )
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="font-bold block" style={{ color: 'var(--text)' }}>
                  {t('aiops.guard.upl')}
                </label>
                <p className="leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                  {t('aiops.guard.upl_body')}
                </p>
              </div>

              <div
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: 'var(--accent-soft)',
                  border: '1px solid var(--accent-border)',
                  color: 'var(--accent-ink)'
                }}
              >
                <strong>{t('aiops.guard.trail')}</strong> {t('aiops.guard.trail_body')}
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowGuardrailsModal(false)}
              >
                {t('aiops.close')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
